import { IssueTaskDeadlineOption, IssueTaskDoc } from '@gooduncles/gu-app-schema';
import { Alert, DatePicker, Radio, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC } from 'react';

import { errorObjectToString } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import { updateIssueTask } from 'src/utils/firebase/firebase-short-cut';
import { getIssueTaskDeadlineInitialValue } from 'src/utils/issue/issue-util';

import IssueCardSubTitle from 'src/atomic-components/atoms/IssueCardItem/IssueCardSubTitle';

import styles from './IssueDeadlineSelector.module.scss';

interface IssueDeadlineSelectorProps {
  issueTaskDoc: IssueTaskDoc;
  editable?: boolean;
}

const IssueDeadlineSelector: FC<IssueDeadlineSelectorProps> = ({ issueTaskDoc, editable = true }) => {
  const currentDeadline = issueTaskDoc.deadline ?? { option: IssueTaskDeadlineOption.ASAP, value: null };

  const handleTypeChange = async (option: IssueTaskDeadlineOption) => {
    if (!editable || currentDeadline.option === option) return;
    const initialValue = getIssueTaskDeadlineInitialValue(option);
    try {
      await updateIssueTask(issueTaskDoc._id, { deadline: { option, value: initialValue } });
    } catch (error) {
      console.error(error);
      notification.error({
        message: '마감일 변경 실패',
        description: errorObjectToString(error),
      });
    }
  };

  const handleDateChange = async (date: Dayjs | null) => {
    if (!editable) return;
    const value = formatDate(date ? date.toDate() : new Date(), 'yyyy-MM-dd');
    try {
      await updateIssueTask(issueTaskDoc._id, { deadline: { option: currentDeadline.option, value } });
    } catch (error) {
      console.error(error);
      notification.error({
        message: '마감일 변경 실패',
        description: errorObjectToString(error),
      });
    }
  };

  return (
    <div className={styles.deadlineSelectorPanel}>
      <IssueCardSubTitle text='마감일 설정' />

      {issueTaskDoc.targetDate && (
        <Alert
          message='적용 날짜가 설정되어 있어 마감일 설정을 변경할 수 없습니다.'
          type='info'
          showIcon
          style={{ margin: '0 8px 4px 8px' }}
        />
      )}

      <div className={styles.optionsContainer}>
        {/* 가능한 빨리 처리 */}
        <div
          className={`${styles.optionCard} ${
            currentDeadline.option === IssueTaskDeadlineOption.ASAP ? styles.selected : ''
          } ${!editable ? styles.disabled : ''}`}
          onClick={editable ? () => handleTypeChange(IssueTaskDeadlineOption.ASAP) : undefined}>
          <div className={styles.radioOption}>
            <Radio checked={currentDeadline.option === IssueTaskDeadlineOption.ASAP} disabled={!editable} />
            <span className={styles.optionTitle}>가능한 빨리 처리</span>
          </div>
          <div className={styles.optionDescription}>최대한 신속하게 처리합니다</div>
        </div>

        {/* 특정 날짜 전까지 */}
        <div
          className={`${styles.optionCard} ${
            currentDeadline.option === IssueTaskDeadlineOption.BEFORE_DATE ? styles.selected : ''
          } ${!editable ? styles.disabled : ''}`}
          onClick={editable ? () => handleTypeChange(IssueTaskDeadlineOption.BEFORE_DATE) : undefined}>
          <div className={styles.radioOption}>
            <Radio checked={currentDeadline.option === IssueTaskDeadlineOption.BEFORE_DATE} disabled={!editable} />
            <span className={styles.optionTitle}>특정 날짜 전까지</span>
          </div>
          <div className={styles.optionDescription}>선택한 날짜 이전에 처리합니다</div>
          {currentDeadline.option === IssueTaskDeadlineOption.BEFORE_DATE && (
            <div className={styles.datePickerContainer}>
              <DatePicker
                value={currentDeadline.value ? dayjs(currentDeadline.value) : null}
                onChange={editable ? handleDateChange : undefined}
                format='YYYY-MM-DD'
                placeholder='날짜를 선택하세요'
                style={{ width: '100%' }}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
                disabled={!editable}
              />
            </div>
          )}
        </div>

        {/* 특정 날짜 지정 */}
        <div
          className={`${styles.optionCard} ${
            currentDeadline.option === IssueTaskDeadlineOption.SPECIFIC_DATE ? styles.selected : ''
          } ${!editable ? styles.disabled : ''}`}
          onClick={editable ? () => handleTypeChange(IssueTaskDeadlineOption.SPECIFIC_DATE) : undefined}>
          <div className={styles.radioOption}>
            <Radio checked={currentDeadline.option === IssueTaskDeadlineOption.SPECIFIC_DATE} disabled={!editable} />
            <span className={styles.optionTitle}>특정 날짜 지정</span>
          </div>
          <div className={styles.optionDescription}>정확히 해당 날짜에 처리합니다</div>
          {currentDeadline.option === IssueTaskDeadlineOption.SPECIFIC_DATE && (
            <div className={styles.datePickerContainer}>
              <DatePicker
                value={currentDeadline.value ? dayjs(currentDeadline.value) : null}
                onChange={editable ? handleDateChange : undefined}
                format='YYYY-MM-DD'
                placeholder='날짜를 선택하세요'
                style={{ width: '100%' }}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
                disabled={!editable}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IssueDeadlineSelector;
export type { IssueDeadlineSelectorProps };
